import React, { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { BASE_URL } from '@/config/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  FileText, Plus, Search, Download, Eye, ArrowUpDown, Trash,
  Calendar, DollarSign, FileSignature, User, Filter, ReceiptIcon, CheckIcon, ChevronDown, Send, Loader2, CreditCard, FileChartLine,
  List,
  Calculator,
  CalculatorIcon,
  Receipt,
  X,
  Repeat,
  RefreshCw,
  Cog,
  SendIcon,
  InfoIcon,
  ListCheck,
  ListEnd,
  ListCollapse,
  ArrowLeft
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { useIsMobile } from '@/hooks/use-mobile';
import { Form, FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form';
import { useAuth } from '@/contexts/AuthContext';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import StudentsSelect from '@/components/invoices/StudentsSelect';
import { eventService } from '@/services/eventService';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { ScrollArea } from '@/components/ui/scroll-area';
import { DateRange as RDPDateRange } from 'react-day-picker';
import { currencies } from '@/utils/ip-detection';
import moment from 'moment';

const INVOICES_STORAGE_KEY = 'invoices-pagination-params';
const STORAGE_TTL = 10 * 60 * 1000; // 10 minutes

const getStoredParams = () => {
  const stored = localStorage.getItem(INVOICES_STORAGE_KEY);
  if (!stored) return null;
  try {
    const data = JSON.parse(stored);
    if (Date.now() - data.timestamp > STORAGE_TTL) {
      localStorage.removeItem(INVOICES_STORAGE_KEY);
      return null;
    }
    if (data.dateRange) {
      if (data.dateRange.from) data.dateRange.from = new Date(data.dateRange.from);
      if (data.dateRange.to) data.dateRange.to = new Date(data.dateRange.to);
    }
    return data;
  } catch (e) {
    return null;
  }
};

type DateRange = Partial<RDPDateRange>;

interface Invoice {
  id: string;
  number: string;
  name: string;
  student: string;
  date: string;
  alertDueDate: boolean;
  dueDate: string;
  amount: number;
  isPaid: boolean;
  invoice_currency: string;
  is_recurring: boolean;
  status: string;
  type?: string; 
  client_type?: string;
  lastSendedEmail?: string;
  emails?: {
    student?: string;
    mother?: string;
    father?: string;
    [key: string]: string | undefined;
  };
}

const invoiceItemSchema = z.object({
  date: z.date(),
  description: z.string().min(1, { message: "Description is required" }),
  amount: z.number().min(0.01, { message: "Amount must be greater than 0" })
});

const additionalFeeSchema = z.object({
  description: z.string().min(1, { message: "Description is required" }),
  amount: z.number().min(0.01, { message: "Amount must be greater than 0" })
});

const invoiceFormSchema = z.object({
  studentId: z.union([z.string(), z.number()]).refine(val => String(val).trim().length > 0, {
    message: "Student is required"
  }),
  items: z.array(invoiceItemSchema).min(1, { message: "At least one item is required" }),
  additionalFees: z.array(additionalFeeSchema).optional().default([])
});

type InvoiceItem = z.infer<typeof invoiceItemSchema>;
type AdditionalFee = z.infer<typeof additionalFeeSchema>;
type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;

interface Student {
  id: string;
  name: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

const Invoices = () => {
  const { t, locale } = useLanguage();
  const { user, token } = useAuth();
  const [savedParams] = useState(() => getStoredParams());

  const [searchTerm, setSearchTerm] = useState(savedParams?.searchTerm || "");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ascending' | 'descending';
  } | null>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(undefined);
  const [selectedStudent, setSelectedStudent] =
  useState<{ id: string; name: string, firstname: string, lastname:string } | undefined>(savedParams?.selectedStudent);

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(savedParams?.selectedStatus);
  const [selectedType, setSelectedType] = useState<string | undefined>(savedParams?.selectedType);
  const [selectedTeacher, setSelectedTeacher] = useState<{ id: string; name: string } | undefined>(savedParams?.selectedTeacher);
  const { toast } = useToast();
  const isCoach = user?.account_type === 'C' || user?.account_type === 'S';
  const isSchool = user?.account_type === 'S';
  const [statusPopoverOpen, setStatusPopoverOpen] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(savedParams?.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(savedParams?.itemsPerPage || 10);
  const [teacherCurrentPage, setTeacherCurrentPage] = useState(savedParams?.teacherCurrentPage || 1);
  const [teacherItemsPerPage, setTeacherItemsPerPage] = useState(savedParams?.teacherItemsPerPage || 10);

  // States for email modal
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<Record<string, boolean>>({});
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState<Invoice | null>(null); 
  const [isCreatingInvoice, setIsCreatingInvoice] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<DateRange>(savedParams?.dateRange || { from: undefined, to: undefined });
  const [manualCurrencyCode, setManualCurrencyCode] = useState(user.currency_code);
  const [newEmail, setNewEmail] = useState("");
  const [createNewEmail, setCreateNewEmail] = useState(false);
  const [selectedClientType, setSelectedClientType] = useState<"student" | "teacher">(() => {
    if (savedParams?.selectedClientType) return savedParams.selectedClientType;
    return (user?.account_type === 'C' || user?.account_type === 'Student' || user?.account_type === 'Parent') ? "student" : "teacher";
  });

  // Reset page when switching client type
  const handleClientTypeChange = (value: "student" | "teacher") => {
    setSelectedClientType(value);
    if (value === "student") setCurrentPage(1);
    else setTeacherCurrentPage(1);
  };
  const [selectedTaxes, setSelectedTaxes] = useState<any[]>([]);
  const [rawValue, setRawValue] = useState("");
  const [selectedYear, setSelectedYear] = useState<string | undefined>(undefined);

  const handleCurrencyChange = (value) => {
    setManualCurrencyCode(value);
  };

  const isSendButtonDisabled = () => {
    const emailsToSend = Object.entries(selectedEmails).filter(([_, isSelected]) => isSelected).map(([type]) => type);
    return sendingInvoice || (emailsToSend.length === 0 && newEmail === "");
  };

  const activePage = selectedClientType === 'teacher' ? teacherCurrentPage : currentPage;
  const activePerPage = selectedClientType === 'teacher' ? teacherItemsPerPage : itemsPerPage;

  const { data: invoicesResponse = {}, isLoading: invoicesLoading, refetch } = useQuery({
  queryKey: [
    'invoices',
    token,
    activePage,
    activePerPage,
    searchTerm,
    selectedStudent,
    selectedTeacher,
    selectedStatus,
    selectedType,
    selectedYear,
    dateRange.from?.toISOString(),
    dateRange.to?.toISOString(),
    sortConfig?.key,
    sortConfig?.direction,
    selectedClientType,
  ],
  queryFn: async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', activePage.toString());
      params.append('per_page', activePerPage.toString());
      // Filtres
      if (searchTerm) params.append('search', searchTerm);
      if (selectedStudent) params.append('student', selectedStudent.id);
      if (selectedStatus) params.append('status', selectedStatus); // <-- Statut "payé", "pending", etc.
      if (selectedType) params.append('type', selectedType);
      if (selectedYear) params.append('year', selectedYear);
      if (selectedTeacher) params.append('teacher', selectedTeacher.id);
      if (dateRange.from) params.append('start_date', dateRange.from.toISOString());
      if (dateRange.to) params.append('end_date', dateRange.to.toISOString());
      if (sortConfig?.key) params.append('sort_by', sortConfig.key);
      if (sortConfig?.direction) params.append('sort_dir', sortConfig.direction);
      if (selectedClientType) params.append('client_type', selectedClientType);

      const response = await fetch(`${BASE_URL}/api/invoices/get?${params.toString()}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return { invoices: [], total: 0, current_page: 1, last_page: 1 };
    }
  },
});

  //console.log('Invoices Response:', invoicesResponse);
  const invoicesData: Invoice[] = invoicesResponse.invoices || [];

    /*const uniqueYears = React.useMemo(() => {
      const years = new Set<string>();
      invoicesData.forEach((invoice: Invoice) => {
        const year = new Date(invoice.date).getFullYear().toString();
        years.add(year);
      });
      return Array.from(years).sort((a, b) => parseInt(b) - parseInt(a)); // Tri par ordre décroissant
    }, [invoicesData]);*/

  const uniqueYears = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 4 }, (_, i) => (currentYear - i).toString());
    return years.sort((a, b) => parseInt(b) - parseInt(a)); // Sort in descending order
  }, []);

  const { data: teachers = [], isLoading: teachersLoading } = useQuery({
    queryKey: ['teachers', token],
    queryFn: async () => {
      if (!isSchool) return [];
      try {
        const response = await fetch(`${BASE_URL}/api/teachers`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          return data.teachers || [];
        }
        return [];
      } catch (error) {
        console.error('Error fetching teachers:', error);
        return [];
      }
    },
  });

  const { data: students = [], isLoading: studentsLoading } = useQuery({
    queryKey: ['students', token],
    queryFn: () => eventService.getStudents(user, token),
  });



  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories', token],
    queryFn: () => eventService.getCategories(user, token),
  });

 const uniqueStudentsFromInvoices = React.useMemo(() => {
  const students = new Map<string, { id: string; name: string }>();
  invoicesData.forEach((invoice: Invoice) => {
    const studentName = invoice.student;
    if (!students.has(studentName)) {
      students.set(studentName, {
        id: invoice.id, // ou un autre identifiant unique si disponible
        name: studentName,
      });
    }
  });
  return Array.from(students.values());
}, [invoicesData]);

  const { data: taxData = [], isLoading: taxesLoading } = useQuery({
    queryKey: ['taxes', token],
    queryFn: async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/billing_taxes`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Accept' : 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch taxes');
        }

        const data = await response.json();
        return data.taxes || [];
      } catch (error) {
        console.error('Error fetching taxes:', error);
        return [];
      }
    },
  });

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      studentId: "",
      items: [{ date: new Date(), description: "", amount: 0 }],
      additionalFees: []
    }
  });

  const translateStr = (key: string): string => {
    const translated = t(key);
    return Array.isArray(translated) ? translated.join(' ') : translated;
  };

  const formatDate = (dateString: string) => {
    const date = moment(dateString);
    //console.log(locale);
    return date.format(locale === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: manualCurrencyCode ? manualCurrencyCode : user.currency_code
    }).format(amount);
  };

  const formatCurrencyInvoice = (currency: string, amount: number) => {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: currency ? currency : user.currency_code
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-600 hover:bg-green-100';
      case 'paid_cash':
        return 'bg-green-100 text-green-600 hover:bg-green-100';
      case 'paid_card':
        return 'bg-green-100 text-green-600 hover:bg-green-100';
      case 'pending':
        return 'bg-yellow-100 text-yellow-600 hover:bg-yellow-100';
      case 'overdue':
        return 'bg-red-100 text-red-600 hover:bg-red-100';
      default:
        return 'bg-gray-100 text-gray-600 hover:bg-gray-100';
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      const response = await fetch(`${BASE_URL}/api/invoice/status`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceId, status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update invoice status');
      }

      refetch();

      toast({
        title: "Invoice updated successfully",
        description: "Invoice status updated successfully",
      });

    } catch (error) {
      console.error('Error updating invoice status:', error);
    } finally {
      setStatusPopoverOpen(null); // Close the popover after updating
    }
  };

  const downloadPDF = async (id, name) => {
    try {
      const response = await fetch(`${BASE_URL}/api/invoice/download/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  const openSendDialog = (invoice: Invoice) => {
    setCurrentInvoice(invoice); // Store the current invoice
    if (invoice.emails) {
      const initialEmails: Record<string, boolean> = {};
      Object.entries(invoice.emails).forEach(([key, email]) => {
        if (email) {
          initialEmails[key] = true;
        }
      });
      setSelectedEmails(initialEmails);
    }
    setShowEmailDialog(true);
  };

  const handleCheckboxChange = (emailType: string, checked: boolean) => {
    setSelectedEmails(prev => ({
      ...prev,
      [emailType]: checked
    }));
  };

  const sendInvoice = async () => {
    if (!currentInvoice) return;

    const emailsToSend = Object.entries(selectedEmails)
      .filter(([_, isSelected]) => isSelected)
      .map(([type]) => type);

    if (emailsToSend.length === 0 && newEmail === "") {
      toast({
        title: t('invoiceDetail.noEmailsSelected'),
        description: t('invoiceDetail.pleaseSelectEmails'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setSendingInvoice(true);
      const response = await fetch(`${BASE_URL}/api/invoice/send/${currentInvoice.id}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
         body: JSON.stringify({
            email_types: emailsToSend,
            new_email: newEmail,
            create_new_email: createNewEmail
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send invoice');
      }

      refetch();

      setShowEmailDialog(false);

      toast({
        title: t('invoiceDetail.sentSuccess'),
        description: t('invoiceDetail.sentSuccessDescription'),
      });

    } catch (error) {
      toast({
        title: t('invoiceDetail.sentError'),
        description: String(error),
        variant: 'destructive',
      });
    } finally {
      setSendingInvoice(false);
    }
  };

  // No need to split client-side anymore - the server filters by client_type
  const showBothTables = isSchool || (isCoach && user?.roleType === "teachers_all");

  

const filteredInvoices = React.useMemo(() => {
  return invoicesData;
}, [invoicesData]);



  const filteredTeacherInvoices = React.useMemo(() => {
    return invoicesData;
  }, [invoicesData]);
  
  

  const requestSort = (key: string) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'ascending'
    ) {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleViewInvoice = (id: string) => {
    // Explicitly save current state before navigating
    const dataToSave = {
      currentPage,
      itemsPerPage,
      teacherCurrentPage,
      teacherItemsPerPage,
      selectedClientType,
      searchTerm,
      selectedStudent,
      selectedTeacher,
      selectedStatus,
      selectedType,
      selectedYear,
      dateRange,
      timestamp: Date.now()
    };
    localStorage.setItem(INVOICES_STORAGE_KEY, JSON.stringify(dataToSave));

    navigate(`/invoices/${id}`);
  };

  const resetFilters = () => {
    setSelectedStudent(undefined);
    setSelectedCategory(undefined);
    setSelectedMonth(undefined);
    setSelectedStatus(undefined);
    setSelectedType(undefined);
    setSelectedTeacher(undefined);
    setSelectedYear(undefined); // Réinitialiser l'année
    setSearchTerm("");
    setDateRange({ from: undefined, to: undefined });

    // Mise à jour immédiate du stockage pour supprimer les filtres tout en gardant la pagination
    const dataToSave = {
      currentPage,
      itemsPerPage,
      teacherCurrentPage,
      teacherItemsPerPage,
      selectedClientType,
      searchTerm: "",
      selectedStudent: undefined,
      selectedTeacher: undefined,
      selectedStatus: undefined,
      selectedType: undefined,
      selectedYear: undefined,
      dateRange: { from: undefined, to: undefined },
      timestamp: Date.now()
    };
    localStorage.setItem(INVOICES_STORAGE_KEY, JSON.stringify(dataToSave));
  };


  useEffect(() => {
    const dataToSave = {
      currentPage,
      itemsPerPage,
      teacherCurrentPage,
      teacherItemsPerPage,
      selectedClientType,
      searchTerm,
      selectedStudent,
      selectedTeacher,
      selectedStatus,
      selectedType,
      selectedYear,
      dateRange,
      timestamp: Date.now()
    };

    localStorage.setItem(
      INVOICES_STORAGE_KEY,
      JSON.stringify(dataToSave)
    );
  }, [currentPage,
      itemsPerPage,
      teacherCurrentPage,
      teacherItemsPerPage,
      selectedClientType,
      searchTerm,
      selectedStudent,
      selectedTeacher,
      selectedStatus,
      selectedType,
      selectedYear,
      dateRange,
      selectedTeacher]);

  const addInvoiceItem = () => {
    const currentItems = form.getValues().items || [];
    form.setValue('items', [
      ...currentItems,
      { date: new Date(), description: "", amount: 0 }
    ]);
  };

  const removeInvoiceItem = (index: number) => {
    const currentItems = form.getValues().items;
    if (currentItems.length > 1) {
      form.setValue('items', currentItems.filter((_, i) => i !== index));
    }
  };

  const addAdditionalFee = () => {
    const currentFees = form.getValues().additionalFees || [];
    form.setValue('additionalFees', [
      ...currentFees,
      { description: "", amount: 0 }
    ]);
  };

  const removeAdditionalFee = (index: number) => {
    const currentFees = form.getValues().additionalFees || [];
    form.setValue('additionalFees', currentFees.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const expenses = calculateExpenses();
    const taxesAmount = calculateTaxes();

    return subtotal + expenses + taxesAmount;
  };

  const calculateTaxes = () => {
    const subtotal = calculateSubtotal();
    return selectedTaxes.reduce((total, tax) => {
      return total + (subtotal * tax.rate / 100);
    }, 0);
  };

  const calculateSubtotal = () => {
    const items = form.getValues().items || [];
    return items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  };
  
  const calculateExpenses = () => {
    const additionalFees = form.getValues().additionalFees || [];
    return additionalFees.reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0);
  };

  useEffect(() => {
    if (selectedStudentIds.length > 0) {
      form.setValue('studentId', String(selectedStudentIds));
      console.log('le student id is', selectedStudentIds);
    } else {
      form.setValue('studentId', '');
    }
  }, [selectedStudentIds, form]);

  useEffect(() => {
    if (taxData.length > 0) {
      // Pre-select all taxes by default
      setSelectedTaxes(taxData);
    }
  }, [taxData]);



  const onSubmit = async (data) => {
    setIsCreatingInvoice(true);
       const subtotal = calculateSubtotal();
        const totalTaxesAmount = calculateTaxes();
        const taxesWithAmount = selectedTaxes.map(tax => {
          const taxAmount = subtotal * (tax.rate / 100);
          return {
            ...tax,
            amount: taxAmount,
          };
        });
    try {
      const response = await fetch(`${BASE_URL}/api/invoices/manual/create`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({...data,
        currencyCode: manualCurrencyCode,
        taxes: taxesWithAmount}),
      });
  
      if (!response.ok) {
        throw new Error('Failed to create invoice');
      }
  
      const result = await response.json();
  
      const invoiceIds = result.invoiceIds;

      toast({
        title: "Invoice created successfully",
        description: "The invoice(s) have been created successfully.",
      });

      setIsDialogOpen(false);
      setIsCreatingInvoice(false);

      refetch();

      if (invoiceIds.length === 1) {
        navigate('/invoices/' + invoiceIds[0]);
      } else {
        navigate('/invoices');
      }


    } catch (error) {
      setIsCreatingInvoice(false);
      console.error('Error creating invoice:', error.message);
      toast({
        title: "Error creating invoice",
        description: String(error),
        variant: 'destructive',
      });
    }
  };
  

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 container py-8 mt-16">

            <div>
              <Button
                variant="ghost"
                onClick={() => navigate('/agenda')}
                className="mb-4 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {translateStr('common.back')}
              </Button>
            </div>

         <Card className="border-0 rounded-none shadow-none p-1 md:border md:rounded-xl md:shadow-sm md:p-0">
          <CardHeader className="p-0 md:p-4">
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center mb-1">
                {!isMobile && (
                  <div className="flex gap-2">
                    <CardTitle>{translateStr('invoices.title')}</CardTitle>
                  </div>
                )}

                {!isMobile && (

                <div className="w-full">
                  {/* Desktop: centered compact toggle */}
                  <div className="hidden md:flex w-full items-center justify-center">
                    {showBothTables && (
                      <div className={cn('inline-flex items-center rounded-full border border-border/70 bg-background/80 p-1 shadow-sm mx-auto', 'max-w-[420px]')}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-pressed={selectedClientType === 'student'}
                          className={cn(
                            'h-9 rounded-full px-4 text-sm font-medium transition-all min-w-[160px] justify-center',
                            selectedClientType === 'student'
                              ? 'bg-primary/95 text-primary-foreground shadow-sm hover:bg-primary/95'
                              : 'text-muted-foreground hover:bg-muted/5'
                          )}
                          onClick={() => handleClientTypeChange('student')}
                        >
                          <User className="mr-2 h-4 w-4" />
                          {translateStr('invoices.studentInvoices')}
                        </Button>

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-pressed={selectedClientType === 'teacher'}
                          className={cn(
                            'h-9 rounded-full px-4 text-sm font-medium transition-all min-w-[160px] justify-center',
                            selectedClientType === 'teacher'
                              ? 'bg-primary/95 text-primary-foreground shadow-sm hover:bg-primary/95'
                              : 'text-muted-foreground hover:bg-muted/5'
                          )}
                          onClick={() => handleClientTypeChange('teacher')}
                        >
                          <List className="mr-2 h-4 w-4" />
                          {translateStr('invoices.teacherInvoices')}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Mobile: full-width stacked selector below the title */}
                  {showBothTables && (
                    <div className="md:hidden mt-3 w-full">
                      <div className="inline-flex w-full items-center rounded-full border border-border/70 bg-background/80 p-1 shadow-sm">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-pressed={selectedClientType === 'student'}
                          className={cn(
                            'h-10 rounded-full px-4 text-sm font-medium flex-1 justify-center',
                            selectedClientType === 'student'
                              ? 'bg-primary/95 text-primary-foreground shadow-sm hover:bg-primary/95'
                              : 'text-muted-foreground hover:bg-muted/5'
                          )}
                          onClick={() => handleClientTypeChange('student')}
                        >
                          <User className="mr-2 h-4 w-4" />
                          {translateStr('invoices.studentInvoices')}
                        </Button>

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-pressed={selectedClientType === 'teacher'}
                          className={cn(
                            'h-10 rounded-full px-4 text-sm font-medium flex-1 justify-center',
                            selectedClientType === 'teacher'
                              ? 'bg-primary/95 text-primary-foreground shadow-sm hover:bg-primary/95'
                              : 'text-muted-foreground hover:bg-muted/5'
                          )}
                          onClick={() => handleClientTypeChange('teacher')}
                        >
                          <List className="mr-2 h-4 w-4" />
                          {translateStr('invoices.teacherInvoices')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                )}



               {isMobile && showBothTables && (
                  <div className="md:hidden mt-3 w-full">
                    <select
                      value={selectedClientType}
                      onChange={(e) => handleClientTypeChange(e.target.value as 'student' | 'teacher')}
                      className="w-full h-10 px-3 py-2 text-sm font-medium rounded-md border border-blue-200 bg-blue-50 text-blue-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231d4ed8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '16px'
                      }}
                    >
                      <option value="student" className="bg-background text-foreground">
                        {translateStr('invoices.studentInvoices')}
                      </option>
                      <option value="teacher" className="bg-background text-foreground">
                        {translateStr('invoices.teacherInvoices')}
                      </option>
                    </select>
                  </div>
                )}
                

              </div>
              <div className={`flex ${isMobile ? 'flex-col gap-3' : 'flex-row items-center gap-4 justify-between'} bg-muted/50 rounded-md border p-3 px-4`}>
                <div className="flex flex-1 gap-2">
                    <div className="relative md:w-46 w-64">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder={translateStr('invoices.search')}
                      className="pl-8"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                        setTeacherCurrentPage(1);
                      }}
                    />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <Filter className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[220px]">
                      <DropdownMenuLabel>{translateStr('invoices.filters')}</DropdownMenuLabel>
                      <DropdownMenuSeparator />

                      <div className="p-2">
                        {(user?.account_type === "C" || user?.account_type === "S") && (
                          <div className="mb-2">
                            <p className="text-xs mb-1 font-medium">{translateStr('invoices.student')}</p>
                            <Select
                              value={selectedStudent?.id}
                              onValueChange={(value) => {
                                const selected = students.find(
                                  (student) => student.id === value
                                );
                                setSelectedStudent(selected);
                                setCurrentPage(1);
                                setTeacherCurrentPage(1);
                              }}
                            >
                              <SelectTrigger className="w-full h-8 text-xs">
                                <SelectValue placeholder={translateStr('invoices.filter.allStudents')} />
                              </SelectTrigger>
                              <SelectContent>
                                {students.map((student) => (
                                  <SelectItem
                                    key={student.id}
                                    value={student.id}
                                  >
                                    {student.firstname + ' ' + student.lastname}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}


                          {showBothTables && user.account_type === 'S' && (
                          <div className="mb-2">
                            <p className="text-xs mb-1 font-medium">{t('invoices.teacher')}</p>
                            <Select 
                              value={selectedTeacher?.id} 
                              onValueChange={(value) => {
                                const teacher = teachers.find((t: any) => t.id === value);
                                setSelectedTeacher(teacher ? { id: teacher.id, name: `${teacher.firstname} ${teacher.lastname}` } : undefined);
                                setCurrentPage(1);
                                setTeacherCurrentPage(1);
                              }}
                            >
                              <SelectTrigger className="w-full h-8 text-xs">
                                <SelectValue placeholder={t('invoices.filter.allTeachers')}>
                                  {selectedTeacher ? selectedTeacher.name : t('invoices.filter.allTeachers')}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {teachers.map((teacher:any) => (
                                  <SelectItem key={teacher.id} value={teacher.id}>
                                    {teacher.firstname} {teacher.lastname}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <div className="mb-2">
                          <p className="text-xs mb-1 font-medium">{translateStr('invoices.filter.status')}</p>
                          <Select value={selectedStatus} onValueChange={(value) => {
                            setSelectedStatus(value || undefined);
                            setCurrentPage(1);
                            setTeacherCurrentPage(1);
                          }}>
                            <SelectTrigger className="w-full h-8 text-xs">
                              <SelectValue placeholder={translateStr('invoices.filter.allStatuses')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="paid">{translateStr('invoices.status.paid')}</SelectItem>
                              <SelectItem value="paid_cash">{translateStr('invoices.status.paid_cash')}</SelectItem>
                              <SelectItem value="paid_card">{translateStr('invoices.status.paid_card')}</SelectItem>
                              <SelectItem value="pending">{translateStr('invoices.status.pending')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="mb-2">
                          <p className="text-xs mb-1 font-medium">{translateStr('invoices.filter.type')}</p>
                          <Select value={selectedType} onValueChange={(value) => {
                            setSelectedType(value || undefined);
                            setCurrentPage(1);
                            setTeacherCurrentPage(1);
                          }}>
                            <SelectTrigger className="w-full h-8 text-xs">
                              <SelectValue placeholder={translateStr('invoices.filter.allTypes')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manual">{translateStr('invoices.type.manual')}</SelectItem>
                              <SelectItem value="automatic">{translateStr('invoices.type.automatic')}</SelectItem>
                              <SelectItem value="shop">{translateStr('invoices.type.shop')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="mb-2">
                          <p className="text-xs mb-1 font-medium">{translateStr('invoices.filter.year')}</p>
                          <Select value={selectedYear} onValueChange={(value) => {
                            setSelectedYear(value || undefined);
                            setCurrentPage(1);
                            setTeacherCurrentPage(1);
                          }}>
                            <SelectTrigger className="w-full h-8 text-xs">
                              <SelectValue placeholder={translateStr('invoices.filter.allYears')} />
                            </SelectTrigger>
                            <SelectContent>
                              {uniqueYears.map((year) => (
                                <SelectItem key={year} value={year}>
                                  {year}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>


                        <div className="mb-2">
                          <p className="text-xs mb-1 font-medium">{translateStr('invoices.filter.date')}</p>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full h-8 text-xs justify-start font-normal"
                                size="sm"
                              >
                                {dateRange.from && dateRange.to ? (
                                  <>
                                    {format(dateRange.from, 'dd/MM/yyyy')} - {format(dateRange.to, 'dd/MM/yyyy')}
                                  </>
                                ) : (
                                  <span>{translateStr('invoices.filter.selectDate')}</span>
                                )}
                                <Calendar className="ml-auto h-4 w-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <CalendarComponent
                                mode="range"
                                selected={dateRange as RDPDateRange}
                                onSelect={(range) => {
                                  setDateRange(range || { from: undefined, to: undefined });
                                  setCurrentPage(1);
                                  setTeacherCurrentPage(1);
                                }}
                                initialFocus
                                numberOfMonths={2}
                                className={cn("p-3 pointer-events-auto")}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <Button className="w-full mt-2" size="sm" variant="ghost" onClick={resetFilters}>
                          {translateStr('invoices.filter.resetFilters')}
                        </Button>
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  
               



                  


                    


                    {/* Dropdown for billing actions when showing both tables */}
                    {isCoach && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='outline'>
                          <ListCollapse className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate('/invoices/report')}>
                          <FileChartLine className="h-4 w-4 mr-2" />
                          {translateStr('invoices.report')} 
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/invoices/pending-validation')}>
                          <ListCheck className="h-4 w-4 mr-2" />
                          {translateStr('invoices.pendingValidation')}
                        </DropdownMenuItem>
                        {invoicesResponse.total_recurring > 0 && (
                          <DropdownMenuItem onClick={() => navigate('/invoices/recurring')}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {translateStr('invoices.recurringInvoices')}
                          </DropdownMenuItem>
                        )}

                      </DropdownMenuContent>
                    </DropdownMenu>
                    )}
                  





                  {/*invoicesResponse.total_recurring > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => navigate('/invoices/recurring')}
                      className="w-full md:w-auto bg-blue-50"
                    >
                      <RefreshCw className="h-4 w-4" />
                      {translateStr('invoices.recurringInvoices')} 
                    </Button>
                  )*/}


                  {/*isCoach && (
                    <Button
                      variant="outline"
                      onClick={() => navigate('/invoices/report')}
                      className="md:w-auto"
                      size={isMobile ? "icon" : undefined}
                    >
                      <FileChartLine className="h-4 w-4" />
                      {!isMobile ? translateStr('invoices.report') : ''}
                    </Button>
                  )*/}


                  </div>
               


                  

                  

                  {((isCoach && selectedClientType === "student") || (user.account_type === "C" && user.roleType === "teachers_all")) && (
                   <Button
                      variant='outline'
                      onClick={() => navigate('/invoices/manual-create')}
                      className="md:w-auto"
                    >
                      <Plus className="h-4 w-4" />
                      {translateStr('invoices.createNew')}
                    </Button>
                  )}

                  {isSchool && selectedClientType === "teacher" && (
                   <Button
                      variant='outline'
                      onClick={() => navigate('/invoices/manual-teacher-create')}
                      className="md:w-auto"
                    >
                      <Plus className="h-4 w-4" />
                      {translateStr('invoices.createNew')}
                    </Button>
                  )}

                  {((showBothTables && selectedClientType === "student") || (user.account_type === "C" && user.roleType === "teachers_all")) && (isSchool || isCoach) && (
                    <Button
                      onClick={() => navigate('/invoices/billing')}
                      className="md:w-auto"
                    >
                      <ReceiptIcon className="h-4 w-4" />
                      {translateStr('invoices.billingStudents')}
                    </Button>
                  )}

                  {/* Teachers billing button */}
                  {showBothTables && selectedClientType === "teacher" && isSchool && (
                    <Button
                      onClick={() => navigate('/school/invoices/billing')}
                      className="w-full md:w-auto"
                    >
                      <ReceiptIcon className="h-4 w-4 mr-2" />
                      {translateStr('invoices.billingTeachers')} 
                    </Button>
                  )}
                

                  {user.account_type === "C" && user.roleType === "teachers_admin" && selectedClientType === "student" && (isSchool || isCoach) && (
                    <Button
                      onClick={() => navigate('/invoices/billing')}
                      className="w-full md:w-auto"
                      size={isMobile ? "icon" : undefined}
                    >
                      <ReceiptIcon className="h-4 w-4" />
                      {translateStr('invoices.billingStudents')}
                    </Button>
                  )}

                   </div>


                  

               
                  {/* Dropdown for billing actions when showing both tables */}
                  {/*isCoach && selectedClientType === "student" && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button className="w-full md:w-auto">
                          <ReceiptIcon className="h-4 w-4 mr-2" />
                          {translateStr('invoices.billingStudents')}
                          <ChevronDown className="h-4 w-4 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate('/invoices/billing')}>
                          <ReceiptIcon className="h-4 w-4 mr-2" />
                          {translateStr('invoices.billingStudents')} 
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/invoices/manual-create')}>
                          <Plus className="h-4 w-4 mr-2" />
                          {translateStr('invoices.createNew')}
                        </DropdownMenuItem>
                        {invoicesResponse.total_recurring > 0 && (
                          <DropdownMenuItem onClick={() => navigate('/invoices/recurring')}>
                            <Repeat className="h-4 w-4 mr-2" />
                            {translateStr('invoices.recurringInvoices')}
                          </DropdownMenuItem>
                        )}

                      </DropdownMenuContent>
                    </DropdownMenu>
                  )*/}


                  
              
            </div>
          </CardHeader>

          {(user?.account_type !== "S" && selectedClientType === "student" || (showBothTables && selectedClientType === "student")) && (
          <CardContent className="p-0 md:p-4 md:pt-0 mb-5 mt-3">
            {/* Affichage des filtres actifs pour les élèves */}
            {(searchTerm || selectedStudent || selectedStatus || selectedType || selectedYear || selectedTeacher || (dateRange.from && dateRange.to)) && (
              <div className="flex flex-wrap gap-2 mb-4 items-center">
                <span className="text-xs font-medium text-muted-foreground mr-1">{t('agenda.activeFilters')}:</span>
                
                {searchTerm && (
                  <Badge variant="secondary" className="flex items-center gap-1 h-7">
                    {t('common.search')}: {searchTerm}
                    <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSearchTerm("")} />
                  </Badge>
                )}

                {selectedStudent && (
                  <Badge variant="secondary" className="flex items-center gap-1 h-7">
                    {t('invoices.student')}: {selectedStudent.firstname + ' ' + selectedStudent.lastname}
                    <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedStudent(undefined)} />
                  </Badge>
                )}

                {selectedTeacher && (
                  <Badge variant="secondary" className="flex items-center gap-1 h-7">
                    {t('invoices.teacher')}: {selectedTeacher?.name}
                    <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedTeacher(undefined)} />
                  </Badge>
                )}

                {selectedStatus && (
                  <Badge variant="secondary" className="flex items-center gap-1 h-7">
                    {t('invoices.filter.status')}: {translateStr(`invoices.status.${selectedStatus}`)}
                    <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedStatus(undefined)} />
                  </Badge>
                )}

                {selectedType && (
                  <Badge variant="secondary" className="flex items-center gap-1 h-7">
                    {t('invoices.filter.type')}: {translateStr(`invoices.type.${selectedType}`)}
                    <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedType(undefined)} />
                  </Badge>
                )}

                {selectedYear && (
                  <Badge variant="secondary" className="flex items-center gap-1 h-7">
                    {t('invoices.filter.year')}: {selectedYear}
                    <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedYear(undefined)} />
                  </Badge>
                )}

                {dateRange.from && dateRange.to && (
                  <Badge variant="secondary" className="flex items-center gap-1 h-7">
                    {t('invoices.filter.date')}: {format(dateRange.from, 'dd/MM/yyyy')} - {format(dateRange.to, 'dd/MM/yyyy')}
                    <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setDateRange({ from: undefined, to: undefined })} />
                  </Badge>
                )}
                <Button variant="ghost" size="sm" onClick={resetFilters} className="h-7 px-2 text-xs hover:bg-transparent hover:text-destructive">
                  {t('invoices.filter.resetFilters')}
                </Button>
              </div>
            )}
            <div className="rounded-md border">
              {isMobile ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[70%]">{t('invoices.title')}</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoicesLoading ? (
                      <TableRow>
                        <TableCell colSpan={2} className="h-24 text-center">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin inline-block" />
                        </TableCell>
                      </TableRow>
                    ) : invoicesData.length > 0 ? (
                      invoicesData.map((invoice: Invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="font-semibold">{invoice.student}</p>
                              <p className="text-muted-foreground">{formatDate(invoice.date)}</p>
                              <div className="flex justify-between items-center">
                                <span className="text-sm bg-muted/50 rounded-md w-auto p-1">{formatCurrencyInvoice(invoice.invoice_currency, invoice.amount)}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                          <div className="text-center">

                            <Popover
                              open={statusPopoverOpen === invoice.id}
                              onOpenChange={() => {
                                setStatusPopoverOpen(prev => (prev === invoice.id ? null : invoice.id));
                              }}
                            >
                              <PopoverTrigger asChild>
                                {isCoach && (
                              <Badge
                                variant="outline"
                                className={`${getStatusColor(invoice.status)} text-xs cursor-pointer flex items-center`}
                              >
                                {translateStr(`invoices.status.${invoice.status}`)}
                                <ChevronDown className="ml-1 h-3 w-3" />
                              </Badge>
                                )}

                              </PopoverTrigger>
                              {isCoach ? (
                              <PopoverContent className="w-48">
                                <div className="flex flex-col">

                                  {invoice.alertDueDate && (
                                    <Button
                                      variant="ghost"
                                      className="justify-start bg-red-100"
                                      onClick={() => openSendDialog(invoice)}
                                    >
                                      <SendIcon className="h-4 w-4 mr-1" />
                                      {translateStr('invoices.resend')}
                                    </Button>
                                  )}

                                <Button
                                    variant="ghost"
                                    className="justify-start"
                                    onClick={() => handleStatusChange(invoice.id, '1')}
                                  >
                                    {translateStr('invoices.status.paid')}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    className="justify-start"
                                    onClick={() => handleStatusChange(invoice.id, '3')}
                                  >
                                    {translateStr('invoices.status.paid_card')}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    className="justify-start"
                                    onClick={() => handleStatusChange(invoice.id, '0')}
                                  >
                                     {translateStr('invoices.status.pending')}
                                  </Button>
                                </div>
                              </PopoverContent>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className={`${getStatusColor(invoice.status)} text-xs w-full flex justify-center items-center`}
                                >
                                  {translateStr(`invoices.status.${invoice.status}`)}
                                </Badge>
                              )}
                            </Popover>

                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewInvoice(invoice.id)}
                                title={translateStr('invoices.actions.view')}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => downloadPDF(invoice.id, invoice.name)}
                                title={translateStr('invoices.actions.download')}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              {isCoach && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openSendDialog(invoice)}
                                title={translateStr('invoices.actions.send')}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                              )}
                            </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} className="h-24 text-center">
                          {searchTerm || selectedStudent || selectedMonth || selectedStatus || selectedType
                            ? translateStr('invoices.noResultsFound')
                            : translateStr('invoices.noInvoices')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                    <TableHead
                        className="cursor-pointer"
                        onClick={() => requestSort('date')}
                      >
                        <div className="flex items-center gap-1">
                          {translateStr('invoices.columns.date')}
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer"
                        onClick={() => requestSort('id')}
                      >
                        <div className="flex items-center gap-1">
                          {translateStr('invoices.columns.type')}
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer"
                        onClick={() => requestSort('student')}
                      >
                        <div className="flex items-center gap-1">
                          {isSchool ? translateStr('invoices.columns.client') : translateStr('invoices.columns.client')}
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer text-right"
                        onClick={() => requestSort('amount')}
                      >
                        <div className="flex items-center gap-1 justify-end">
                          {translateStr('invoices.columns.amount')}
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer text-right"
                        onClick={() => requestSort('status')}
                      >
                        <div className="flex items-center gap-1">
                          {translateStr('invoices.columns.status')}
                          <ArrowUpDown className="h-3 w-3" />
                        </div>
                      </TableHead>
                      {isCoach && (
                      <TableHead>
                        <div className="flex items-center gap-1">
                          {translateStr('invoices.columns.send')}
                        </div>
                      </TableHead>
                      )}
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoicesLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                               <Loader2 className="mr-2 h-4 w-4 animate-spin inline-block" />
                        </TableCell>
                      </TableRow>
                    ) : invoicesData.length > 0 ? (
                      invoicesData.map((invoice: Invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>{formatDate(invoice.date)}</TableCell>
                          <TableCell>
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <Badge>{invoice.type}</Badge>
                                {invoice.is_recurring ? (
                                  <RefreshCw
                                    size={14}
                                    style={{
                                      position: 'absolute',
                                      top: -4,
                                      right: -4,
                                      backgroundColor: 'white',
                                      borderRadius: '50%',
                                      padding: '3px'
                                    }}
                                  />
                                ):(null)}
                              </div>
                            </TableCell>
                          <TableCell>{invoice.student}</TableCell>
                          <TableCell className="text-right"><Badge>{formatCurrencyInvoice(invoice.invoice_currency, invoice.amount)}</Badge></TableCell>
                          <TableCell>
                          <Popover
                              open={statusPopoverOpen === invoice.id}
                              onOpenChange={() => {
                                setStatusPopoverOpen(prev => (prev === invoice.id ? null : invoice.id));
                              }}
                            >
                          
                              {isCoach ? (
                                <PopoverTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className={`${getStatusColor(invoice.status)} text-xs cursor-pointer`}
                                  >
                                    {translateStr(`invoices.status.${invoice.status}`)}
                                    <ChevronDown className="ml-1 h-3 w-3" />
                                  </Badge>
                                </PopoverTrigger>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className={`${getStatusColor(invoice.status)} text-xs`}
                                >
                                  {translateStr(`invoices.status.${invoice.status}`)}
                                </Badge>
                              )}


                            
                              <PopoverContent className="w-48">
                                <div className="flex flex-col">

                                  {invoice.alertDueDate && (
                                    <Button
                                      variant="ghost"
                                      className="justify-start bg-red-100"
                                      onClick={() => openSendDialog(invoice)}
                                    >
                                      <SendIcon className="h-4 w-4 mr-1" />
                                      {translateStr('invoices.resend')}
                                    </Button>
                                  )}

                                <Button
                                    variant="ghost"
                                    className="justify-start"
                                    onClick={() => handleStatusChange(invoice.id, '1')}
                                  >
                                    {translateStr('invoices.status.paid')}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    className="justify-start"
                                    onClick={() => handleStatusChange(invoice.id, '2')}
                                  >
                                    {translateStr('invoices.status.paid_cash')}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    className="justify-start"
                                    onClick={() => handleStatusChange(invoice.id, '3')}
                                  >
                                    {translateStr('invoices.status.paid_card')}
                                  </Button>
                                  {!invoice.alertDueDate && (
                                  <Button
                                    variant="ghost"
                                    className="justify-start"
                                    onClick={() => handleStatusChange(invoice.id, '0')}
                                  >
                                    {translateStr('invoices.status.pending')}
                                  </Button>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </TableCell>
                          {isCoach && (
                          <TableCell>
                            <Badge
                              onClick={() => openSendDialog(invoice)}
                              title={translateStr('invoices.actions.send')}
                              className='cursor-pointer'
                            >
                              <Send className="h-4 w-4 mr-1" />
                              {invoice.lastSendedEmail !== null ? t('invoices.sended') : t('invoices.send')} {invoice.lastSendedEmail !== null ? formatDate(invoice.lastSendedEmail) : ""}
                            </Badge>
                          </TableCell>
                          )}
                          <TableCell>
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewInvoice(invoice.id)}
                                title={translateStr('invoices.actions.view')}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => downloadPDF(invoice.id, invoice.name)}
                                title={translateStr('invoices.actions.download')}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                          {searchTerm || selectedStudent || selectedMonth || selectedStatus || selectedType
                            ? translateStr('invoices.noResultsFound')
                            : translateStr('invoices.noInvoices')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}

              {invoicesResponse.invoices && invoicesResponse.invoices.length > 0 && (
                <div className="mt-4">
                {invoicesResponse.last_page > 1 && (
                    <div className="py-2 py-3 border-t px-4 bg-gray-50 border-gray-200 rounded-b-md flex justify-center">
                      <Pagination>
                        <PaginationContent>
                          {/* Bouton Précédent */}
                          <PaginationItem>
                            <PaginationPrevious
                              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                              className={currentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                          </PaginationItem>

                          {/* Première page */}
                          <PaginationItem>
                            <PaginationLink
                              isActive={currentPage === 1}
                              onClick={() => setCurrentPage(1)}
                              className="cursor-pointer"
                            >
                              1
                            </PaginationLink>
                          </PaginationItem>

                          {/* Ellipsis si nécessaire */}
                          {currentPage > 3 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}

                          {/* Pages autour de la page courante */}
                          {Array.from({ length: Math.min(2, invoicesResponse.last_page) }, (_, i) => {
                            let pageNum;
                            if (currentPage <= 2) {
                              pageNum = i + 2; // Pages 2, 3, 4 si on est au début
                            } else if (currentPage >= invoicesResponse.last_page - 1) {
                              pageNum = invoicesResponse.last_page - 2 + i; // Pages avant la fin
                            } else {
                              pageNum = currentPage - 1 + i; // Pages autour de la page courante
                            }
                            // Éviter les doublons avec la première et dernière page
                            if (pageNum > 1 && pageNum < invoicesResponse.last_page) {
                              return (
                                <PaginationItem key={pageNum}>
                                  <PaginationLink
                                    isActive={currentPage === pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    className="cursor-pointer"
                                  >
                                    {pageNum}
                                  </PaginationLink>
                                </PaginationItem>
                              );
                            }
                            return null;
                          })}

                          {/* Ellipsis si nécessaire */}
                          {currentPage < invoicesResponse.last_page - 2 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}

                          {/* Dernière page (si différente de la première) */}
                          {invoicesResponse.last_page > 1 && (
                            <PaginationItem>
                              <PaginationLink
                                isActive={currentPage === invoicesResponse.last_page}
                                onClick={() => setCurrentPage(invoicesResponse.last_page)}
                                className="cursor-pointer"
                              >
                                {invoicesResponse.last_page}
                              </PaginationLink>
                            </PaginationItem>
                          )}

                          {/* Bouton Suivant */}
                          <PaginationItem>
                            <PaginationNext
                              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, invoicesResponse.last_page))}
                              className={currentPage >= invoicesResponse.last_page ? "pointer-events-none opacity-50" : "cursor-pointer"}
                            />
                          </PaginationItem>
                        </PaginationContent>

                      </Pagination>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col md:flex-row mt-4 w-full md:w-auto justify-end items-center">
                <div className="flex items-center gap-2">
                  <Label htmlFor="invoices-per-page" className="text-sm font-normal text-muted-foreground whitespace-nowrap">{t('invoices.perPage', 'Per page')}</Label>
                  <Select
                      value={itemsPerPage.toString()}
                      onValueChange={(value) => {
                          setItemsPerPage(Number(value));
                          setCurrentPage(1);
                      }}
                  >
                      <SelectTrigger className="w-[80px] h-10 text-xs">
                          <SelectValue placeholder={itemsPerPage.toString()} />
                      </SelectTrigger>
                      <SelectContent>
                          <SelectItem value="5">5</SelectItem>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                  </Select>
                </div>
              </div>

          </CardContent>
          )}
       

        {/* Teachers Invoices Section */}
        {(user?.account_type !== "S" && selectedClientType === "teacher" || (showBothTables && selectedClientType === "teacher")) && (
          <div>
                <CardHeader className="p-0 md:p-6 hidden md:block">
              <CardTitle className="text-lg">{t('invoices.teacherInvoices')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0 pt-2 md:p-6 md:pt-0">
              {/* Affichage des filtres actifs pour les professeurs */}
              {(searchTerm || selectedStudent || selectedStatus || selectedType || selectedYear || selectedTeacher || (dateRange.from && dateRange.to)) && (
                <div className="flex flex-wrap gap-2 mb-4 items-center">
                  <span className="text-xs font-medium text-muted-foreground mr-1">{t('agenda.activeFilters')}:</span>
                  
                  {searchTerm && (
                    <Badge variant="secondary" className="flex items-center gap-1 h-7">
                      {t('common.search')}: {searchTerm}
                      <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSearchTerm("")} />
                    </Badge>
                  )}

                  {selectedStudent && (
                    <Badge variant="secondary" className="flex items-center gap-1 h-7">
                      {t('invoices.student')}: {selectedStudent.name}
                      <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedStudent(undefined)} />
                    </Badge>
                  )}

                  {selectedTeacher && (
                    <Badge variant="secondary" className="flex items-center gap-1 h-7">
                      {t('invoices.teacher')}: {selectedTeacher?.name}
                      <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedTeacher(undefined)} />
                    </Badge>
                  )}

                  {selectedStatus && (
                    <Badge variant="secondary" className="flex items-center gap-1 h-7">
                      {t('invoices.filter.status')}: {translateStr(`invoices.status.${selectedStatus}`)}
                      <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedStatus(undefined)} />
                    </Badge>
                  )}

                  {selectedType && (
                    <Badge variant="secondary" className="flex items-center gap-1 h-7">
                      {t('invoices.filter.type')}: {translateStr(`invoices.type.${selectedType}`)}
                      <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedType(undefined)} />
                    </Badge>
                  )}

                  {selectedYear && (
                    <Badge variant="secondary" className="flex items-center gap-1 h-7">
                      {t('invoices.filter.year')}: {selectedYear}
                      <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setSelectedYear(undefined)} />
                    </Badge>
                  )}

                  {dateRange.from && dateRange.to && (
                    <Badge variant="secondary" className="flex items-center gap-1 h-7">
                      {t('invoices.filter.date')}: {format(dateRange.from, 'dd/MM/yyyy')} - {format(dateRange.to, 'dd/MM/yyyy')}
                      <X className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => setDateRange({ from: undefined, to: undefined })} />
                    </Badge>
                  )}
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="h-7 px-2 text-xs hover:bg-transparent hover:text-destructive">
                    {t('invoices.filter.resetFilters')}
                  </Button>
                </div>
              )}
              <div className="rounded-md border">
                {isMobile ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[70%]">{t('invoices.title')}</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                    {invoicesLoading ? (
                      <TableRow>
                        <TableCell colSpan={2} className="h-24 text-center">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin inline-block" />
                        </TableCell>
                      </TableRow>
                    ) : filteredTeacherInvoices.length > 0 ? (
                      filteredTeacherInvoices.map((invoice: Invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="text-sm">{invoice.student}</p>
                              <p className="text-sm text-muted-foreground">{formatDate(invoice.date)}</p>
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-semibold">{formatCurrencyInvoice(invoice.invoice_currency, invoice.amount)}</span>
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  <Badge variant="outline" className="ml-2 text-xs bg-blue-50 text-blue-700">Teacher</Badge>
                                  {invoice.is_recurring ? (
                                    <RefreshCw
                                      size={14}
                                      style={{
                                        position: 'absolute',
                                        top: -4,
                                        right: -4,
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        padding: '3px'
                                      }}
                                    />
                                  ):(null)}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-center">
                              <Badge
                                variant="outline"
                                className={`${getStatusColor(invoice.status)} text-xs mb-2`}
                              >
                                {translateStr(`invoices.status.${invoice.status}`)}
                              </Badge>
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewInvoice(invoice.id)}
                                  title="View"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => downloadPDF(invoice.id, invoice.name)}
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} className="h-24 text-center">
                          {searchTerm || selectedTeacher || selectedStatus || selectedType
                            ? translateStr('invoices.noResultsFound')
                            : translateStr('invoices.noInvoices')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  </Table>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Teacher</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoicesLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="h-24 text-center">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin inline-block" />
                          </TableCell>
                        </TableRow>
                      ) : filteredTeacherInvoices.length > 0 ? (
                        filteredTeacherInvoices.map((invoice: Invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell>{formatDate(invoice.date)}</TableCell>
                            <TableCell>
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <Badge variant="outline" className="bg-blue-50 text-blue-700">{t('invoices.teacherInvoices')}</Badge>
                                {invoice.is_recurring ? (
                                  <RefreshCw
                                    size={14}
                                    style={{
                                      position: 'absolute',
                                      top: -4,
                                      right: -4,
                                      backgroundColor: 'white',
                                      borderRadius: '50%',
                                      padding: '3px'
                                    }}
                                  />
                                ):(null)}
                              </div>
                            </TableCell>
                            <TableCell>{invoice.student}</TableCell>
                            <TableCell className="text-right">{formatCurrencyInvoice(invoice.invoice_currency, invoice.amount)}</TableCell>
                            <TableCell className='w-[1px] whitespace-nowrap'>
                              {user?.account_type === "C" ? (
                                <div className="text-center">
                                  <Popover
                                    open={statusPopoverOpen === invoice.id}
                                    onOpenChange={() => {
                                      setStatusPopoverOpen(prev => (prev === invoice.id ? null : invoice.id));
                                    }}
                                  >
                                    <PopoverTrigger asChild>
                                      <Badge
                                        variant="outline"
                                        className={`${getStatusColor(invoice.status)} text-xs cursor-pointer flex items-center`}
                                      >
                                        {translateStr(`invoices.status.${invoice.status}`)}
                                        <ChevronDown className="ml-1 h-3 w-3" />
                                      </Badge>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-48">
                                      <div className="flex flex-col">
                                        <Button
                                          variant="ghost"
                                          className="justify-start"
                                          onClick={() => handleStatusChange(invoice.id, '1')}
                                        >
                                          {translateStr('invoices.status.paid')}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          className="justify-start"
                                          onClick={() => handleStatusChange(invoice.id, '2')}
                                        >
                                          {translateStr('invoices.status.paid_cash')}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          className="justify-start"
                                          onClick={() => handleStatusChange(invoice.id, '3')}
                                        >
                                          {translateStr('invoices.status.paid_card')}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          className="justify-start"
                                          onClick={() => handleStatusChange(invoice.id, '0')}
                                        >
                                          {translateStr('invoices.status.pending')}
                                        </Button>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className={`${getStatusColor(invoice.status)} text-xs`}
                                >
                                  {translateStr(`invoices.status.${invoice.status}`)}
                                </Badge>
                              )}
                            </TableCell>

                            <TableCell>
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewInvoice(invoice.id)}
                                  title="View"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => downloadPDF(invoice.id, invoice.name)}
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} className="h-24 text-center">
                            {searchTerm || selectedTeacher || selectedStatus || selectedType
                              ? translateStr('invoices.noResultsFound')
                              : translateStr('invoices.noInvoices')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>

              {invoicesResponse.last_page > 1 && (
                <div className="rounded-md border mt-4">
                  <div className="py-3 px-4 bg-muted/50 border-border flex justify-center">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setTeacherCurrentPage((prev) => Math.max(prev - 1, 1))}
                            className={teacherCurrentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>

                        <PaginationItem>
                          <PaginationLink
                            isActive={teacherCurrentPage === 1}
                            onClick={() => setTeacherCurrentPage(1)}
                            className="cursor-pointer"
                          >
                            1
                          </PaginationLink>
                        </PaginationItem>

                        {teacherCurrentPage > 3 && (
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                        )}

                        {Array.from({ length: Math.min(2, invoicesResponse.last_page) }, (_, i) => {
                          let pageNum;
                          if (teacherCurrentPage <= 2) {
                            pageNum = i + 2;
                          } else if (teacherCurrentPage >= invoicesResponse.last_page - 1) {
                            pageNum = invoicesResponse.last_page - 2 + i;
                          } else {
                            pageNum = teacherCurrentPage - 1 + i;
                          }
                          if (pageNum > 1 && pageNum < invoicesResponse.last_page) {
                            return (
                              <PaginationItem key={pageNum}>
                                <PaginationLink
                                  isActive={teacherCurrentPage === pageNum}
                                  onClick={() => setTeacherCurrentPage(pageNum)}
                                  className="cursor-pointer"
                                >
                                  {pageNum}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          }
                          return null;
                        })}

                        {teacherCurrentPage < invoicesResponse.last_page - 2 && (
                          <PaginationItem>
                            <PaginationEllipsis />
                          </PaginationItem>
                        )}
 
                        {invoicesResponse.last_page > 1 && (
                          <PaginationItem>
                            <PaginationLink
                              isActive={teacherCurrentPage === invoicesResponse.last_page}
                              onClick={() => setTeacherCurrentPage(invoicesResponse.last_page)}
                              className="cursor-pointer"
                            >
                              {invoicesResponse.last_page}
                            </PaginationLink>
                          </PaginationItem>
                        )}

                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setTeacherCurrentPage((prev) => Math.min(prev + 1, invoicesResponse.last_page))}
                            className={teacherCurrentPage >= invoicesResponse.last_page ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                </div>
              )}

              <div className="flex flex-col md:flex-row mt-4 w-full md:w-auto justify-end items-center">
                <div className="flex items-center gap-2">
                  <Label htmlFor="teacher-invoices-per-page" className="text-sm font-normal text-muted-foreground whitespace-nowrap">{t('invoices.perPage', 'Per page')}</Label>
                  <Select
                    value={teacherItemsPerPage.toString()}
                    onValueChange={(value) => {
                      setTeacherItemsPerPage(Number(value));
                      setTeacherCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[80px] h-10 text-xs">
                      <SelectValue placeholder={teacherItemsPerPage.toString()} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </div>
        )}
         </Card>
      </main>

      <Footer />

      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
       <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
               <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-2"}>
            <DialogTitle>
              <div className="flex items-center gap-2">
                {t('invoiceDetail.sendToRecipients')}
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className={isMobile ? "py-4 space-y-4 p-5" : "py-4 space-y-4 mb-4"}>

            {currentInvoice && currentInvoice.alertDueDate && (
              <>
                <div className='flex items-center bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-sm'>
                  <InfoIcon className="h-3 w-3 mr-1" />
                  <span>{t('invoices.invoiceWillBeSentWithOverdue')}.</span>
                </div>
              </>
            )}

            <p className='text-sm text-muted-foreground'>{showEmailDialog && currentInvoice !== null && currentInvoice.lastSendedEmail && currentInvoice.lastSendedEmail !== null ? t('invoices.sended') : ""} {showEmailDialog && currentInvoice !== null && currentInvoice.lastSendedEmail && currentInvoice.lastSendedEmail !== null ? formatDate(currentInvoice.lastSendedEmail) : ""}</p>

            <p className="text-sm">{t('invoiceDetail.selectRecipients')}</p>

            {currentInvoice?.emails && Object.entries(currentInvoice.emails).map(([emailType, email]) =>
              email ? (
                <div key={emailType} className="flex items-center space-x-2">
                  <Checkbox
                    id={`email-${emailType}`}
                    checked={!!selectedEmails[emailType]}
                    onCheckedChange={(checked) => handleCheckboxChange(emailType, checked === true)}
                  />
                  <Label htmlFor={`email-${emailType}`} className="flex-1">
                    <span className="capitalize mr-2">{emailType}:</span>
                    <span className="text-muted-foreground">{email}</span>
                  </Label>
                </div>
              ) : null
            )}

            {(!currentInvoice?.emails || Object.values(currentInvoice.emails).filter(Boolean).length === 0) && (
              <span className="text-sm text-muted-foreground">{t('invoiceDetail.noEmailsAvailable')}</span>
            )}

            {(!currentInvoice?.emails || Object.values(currentInvoice?.emails || {}).filter(Boolean).length === 0) && (
                <div className="items-center space-x-2 space-y-4">
                  <Input
                    type="email"
                    placeholder={t('students.email')}
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                  />
                  <Checkbox
                    id="create-new-email"
                    checked={createNewEmail}
                    onCheckedChange={(checked) => setCreateNewEmail(checked === true)}
                  />
                  <Label htmlFor="create-new-email" className='text-xs'>
                    {t('invoiceDetail.createSenderEmail')}
                  </Label>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={sendInvoice} disabled={isSendButtonDisabled()}>
              {sendingInvoice ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.sending')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t('invoiceDetail.sendInvoice')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
                                   